import {
  type CommitmentVersion,
  isSimulateToCommandData,
  type ScenarioVariantSimulationVersionResult,
} from '@festina/life-general-types/life'
import { notEmpty, type Optional } from '@festina-life/core'
import { useDataContext } from '@festina-life/ui/theme'
import type { UseSuspenseQueryResult } from '@tanstack/react-query'

import { useOptionalCommitmentSimulationContext } from '../context/commitmentSimulationContext'
import {
  useScenarioContext,
  variantIdentifierToString,
} from '../context/scenarioContext'
import { useHook } from '../lenteHooks'
import { isCommitmentVersion } from '../utility/isCommitmentVersion'
import { useCommitmentVersionSelection } from './useCommitmentVersionSelection'
import {
  type CurrentCommitmentContextHookVariant,
  useCurrentCommitmentContextVariant,
} from './useCurrentCommitmentContextVariant'
import { useFetchCommitmentVersionByDate } from './useFetchCommitmentVersionByDate'
import {
  type PreviousCommitmentContextHookVariant,
  usePreviousCommitmentContextVariant,
} from './usePreviousCommitmentContextVariant'

type CommitmentVersionContext<T extends CommitmentVersion> = {
  currentCommitmentVersion?: T
  previousCommitmentVersion?: T
  isDiffing: boolean
}

type Props = {
  commitmentDate?: Optional<string>
  currentOverrideCommitmentVariant?: CurrentCommitmentContextHookVariant
}

const getCommitmentVersionFromScenarioSimulation = ({
  simulation,
  variantIdentifier,
}: {
  simulation: Optional<ScenarioVariantSimulationVersionResult>
  variantIdentifier: {
    commandId?: string
  }
}) => {
  const isVariantSimulation = !variantIdentifier.commandId

  const doesNotHaveCommandVersion =
    simulation?.commandSimulationVersionResults?.every(
      (result) => !result?.commandVersion,
    )
  if (
    doesNotHaveCommandVersion &&
    simulation?.commandSimulationVersionResults?.length === 1
  ) {
    return simulation.commandSimulationVersionResults[0]?.commitmentVersion
  }

  return simulation?.commandSimulationVersionResults?.find((result) => {
    const { commandVersion } = result ?? {}
    return isVariantSimulation
      ? commandVersion?.commandType === 'simulateTo' &&
          isSimulateToCommandData(commandVersion.commandData) &&
          !!commandVersion.commandData.isFinalSimulateTo
      : result?.commandVersion?.commandId === variantIdentifier.commandId
  })?.commitmentVersion
}

export const useCommitmentVersion = <T extends CommitmentVersion>({
  commitmentDate,
  currentOverrideCommitmentVariant,
}: Props = {}): CommitmentVersionContext<T> => {
  const commitmentSimulationContext = useOptionalCommitmentSimulationContext()
  const { ownerId, tenantId } = useHook('routing.general.useEntityParams')()
  const { previousEventId, expectedApplyDate } = useDataContext()
  const {
    variantIdentifier,
    compareVariantIdentifier,
    useRealBenefit,
    simulationDate,
  } = useScenarioContext()
  const {
    currentSelection,
    previousSelection,
    currentSelectionSimulationDate,
    previousSelectionSimulationDate,
  } = useCommitmentVersionSelection()

  const scenarioIdForRequestHeader = variantIdentifier
    ? variantIdentifierToString(variantIdentifier)
    : undefined
  const previousScenarioIdForRequestHeader = compareVariantIdentifier
    ? variantIdentifierToString(compareVariantIdentifier)
    : undefined

  const currentCommitmentVariant = useCurrentCommitmentContextVariant({
    commitmentDate,
    currentOverrideCommitmentVariant,
  })

  const previousCommitmentVariant = usePreviousCommitmentContextVariant()

  const currentDefaultEvent = useHook(
    'fetchers.life.useFetchCommitmentVersion',
  )(
    {
      eventId: currentSelection?.id ?? '',
      ownerId,
      tenantId,
      scenarioId: scenarioIdForRequestHeader,
    },
    {
      enabled: currentCommitmentVariant === 'currentDefaultEvent',
      retry: false,
    },
  )

  const previousEvent = useHook('fetchers.life.useFetchCommitmentVersion')(
    {
      eventId: previousSelection?.id ?? '',
      ownerId,
      tenantId,
      scenarioId: previousScenarioIdForRequestHeader,
    },
    {
      enabled: previousCommitmentVariant === 'previousEvent',
      retry: false,
    },
  )

  const currentDefaultSimulation = useHook(
    'fetchers.life.useFetchCommitmentVersionSimulation',
  )(
    {
      ownerId,
      tenantId,
      requestBody: {
        _type: 'CommitmentSimulationData',
        commands: [],
        simulateTo: currentSelectionSimulationDate,
      },
      scenarioId: scenarioIdForRequestHeader,
    },
    {
      enabled: currentCommitmentVariant === 'currentDefaultSimulation',
      retry: false,
    },
  )

  const previousSimulation = useHook(
    'fetchers.life.useFetchCommitmentVersionSimulation',
  )(
    {
      ownerId,
      tenantId,
      requestBody: {
        _type: 'CommitmentSimulationData',
        commands: [],
        simulateTo: previousSelectionSimulationDate,
      },
      scenarioId: previousScenarioIdForRequestHeader,
    },
    {
      enabled: previousCommitmentVariant === 'previousSimulation',
      retry: false,
    },
  )

  const currentFutureCommand = useHook(
    'fetchers.life.useFetchCommitmentVersionSimulation',
  )(
    {
      ownerId,
      tenantId,
      requestBody: {
        _type: 'CommitmentSimulationData',
        simulateTo: expectedApplyDate,
        commands: [],
      },
      scenarioId: scenarioIdForRequestHeader,
    },
    {
      enabled: currentCommitmentVariant === 'currentFutureCommand',
      retry: false,
    },
  )

  const currentScenarioSimulation = useHook(
    'fetchers.life.useFetchScenarioVariantSimulation',
  )(
    {
      ownerId,
      tenantId,
      scenarioId: variantIdentifier?.scenarioId ?? '',
      scenarioVariantId: variantIdentifier?.variantId ?? '',
      // Crashes on empty strings therefor this check is here
      advanceTo: simulationDate ? simulationDate : undefined,
      useRealBenefit,
    },
    {
      enabled: currentCommitmentVariant === 'currentScenarioSimulation',
      select: (scenarioVariantSimulationVersionResult) =>
        variantIdentifier
          ? getCommitmentVersionFromScenarioSimulation({
              simulation: scenarioVariantSimulationVersionResult,
              variantIdentifier,
            })
          : undefined,
    },
  )

  const previousScenarioSimulation = useHook(
    'fetchers.life.useFetchScenarioVariantSimulation',
  )(
    {
      ownerId,
      tenantId,
      scenarioId: compareVariantIdentifier?.scenarioId ?? '',
      scenarioVariantId: compareVariantIdentifier?.variantId ?? '',
      // Crashes on empty strings therefor this check is here
      advanceTo: simulationDate ? simulationDate : undefined,
      useRealBenefit,
    },
    {
      enabled: previousCommitmentVariant === 'previousScenarioSimulation',
      retry: false,
      select: (commitmentVersion) =>
        compareVariantIdentifier
          ? getCommitmentVersionFromScenarioSimulation({
              simulation: commitmentVersion,
              variantIdentifier: compareVariantIdentifier,
            })
          : undefined,
    },
  )

  const currentAppliedCommand = useHook(
    'fetchers.life.useFetchCommitmentVersion',
  )(
    {
      eventId: previousEventId ?? '',
      ownerId,
      tenantId,
      scenarioId: scenarioIdForRequestHeader,
    },
    {
      enabled: currentCommitmentVariant === 'currentAppliedCommand',
      retry: false,
    },
  )

  const currentByCommitmentDate = useFetchCommitmentVersionByDate({
    commitmentDate,
    enabled: currentCommitmentVariant === 'currentByCommitmentDate',
  })

  const currentCommitmentVersionByVariant: Record<
    CurrentCommitmentContextHookVariant,
    UseSuspenseQueryResult<Optional<CommitmentVersion>> | undefined
  > = {
    currentDefaultEvent,
    currentDefaultSimulation,
    currentAppliedCommand,
    currentByCommitmentDate,
    currentFutureCommand,
    currentScenarioSimulation,
    currentNone: undefined,
  }

  const currentCommitmentVersion =
    currentCommitmentVersionByVariant[currentCommitmentVariant]?.data
  const hasCommitmentSimulationContext = notEmpty(
    commitmentSimulationContext?.isDiffing,
  )

  const preferredCurrentCommitmentVersion = hasCommitmentSimulationContext
    ? commitmentSimulationContext.currentVersion
    : currentCommitmentVersion

  const simulateEmptyReality = useHook(
    'fetchers.life.useFetchCommitmentVersionSimulation',
  )(
    {
      ownerId,
      tenantId,
      requestBody: {
        _type: 'CommitmentSimulationData',
        commands: [],
        simulateTo:
          simulationDate ?? preferredCurrentCommitmentVersion?.advanceToDate,
      },
      deflate: useRealBenefit,
    },
    {
      enabled:
        previousCommitmentVariant === 'simulateEmptyReality' &&
        !!preferredCurrentCommitmentVersion,
      retry: false,
    },
  )

  const previousCommitmentVersionByVariant: Record<
    PreviousCommitmentContextHookVariant,
    UseSuspenseQueryResult<Optional<CommitmentVersion>> | undefined
  > = {
    previousEvent,
    previousSimulation,
    previousScenarioSimulation,
    simulateEmptyReality,
    previousNone: undefined,
  }

  const previousCommitmentVersion =
    previousCommitmentVersionByVariant[previousCommitmentVariant]?.data
  const preferredPreviousCommitmentVersion = hasCommitmentSimulationContext
    ? commitmentSimulationContext.previousVersion
    : previousCommitmentVersion

  const preferredIsDiffing = Boolean(
    commitmentSimulationContext.previousVersion
      ? commitmentSimulationContext.isDiffing
      : isCommitmentVersion<T>(currentCommitmentVersion) &&
          isCommitmentVersion<T>(previousCommitmentVersion),
  )

  return {
    currentCommitmentVersion: isCommitmentVersion<T>(
      preferredCurrentCommitmentVersion,
    )
      ? preferredCurrentCommitmentVersion
      : undefined,
    previousCommitmentVersion: isCommitmentVersion<T>(
      preferredPreviousCommitmentVersion,
    )
      ? preferredPreviousCommitmentVersion
      : undefined,
    isDiffing: preferredIsDiffing,
  }
}
